import { queryOptions, useQuery } from "@tanstack/react-query";

import type { Page } from "@/api/api.types";
import {
    SHARED_CONVERSATION_LOOKUP_ROOT,
    SHARED_CONVERSATIONS_KEY,
    SIDEBAR_PAGE_SIZE,
} from "@/api/chat/conversationListKeys";
import { signalOnceCached } from "@/api/querySignal";
import {
    getSharedConversations,
    type ISharedConversationListItem,
} from "@/api/shared-conversations";

export const sharedConversationsQueryOptions = queryOptions({
    queryKey: SHARED_CONVERSATIONS_KEY,
    queryFn: (context) =>
        getSharedConversations(1, SIDEBAR_PAGE_SIZE, {
            signal: signalOnceCached(context),
        }),
    staleTime: 60_000,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
});

export const sharedConversationQueryOptions = (remoteId: string) =>
    queryOptions({
        queryKey: [SHARED_CONVERSATION_LOOKUP_ROOT, remoteId],
        queryFn: async ({
            client,
            signal,
        }): Promise<ISharedConversationListItem | null> => {
            const first = await client.fetchQuery(
                sharedConversationsQueryOptions
            );
            const matches = (item: ISharedConversationListItem) =>
                item.conversation_id.toString() === remoteId;
            const firstMatch = first.items.find(matches);
            if (firstMatch) return firstMatch;
            for (let page = first.page + 1; page <= first.pages; page += 1) {
                const next = await getSharedConversations(page, first.size, {
                    signal,
                });
                const match = next.items.find(matches);
                if (match) return match;
            }
            return null;
        },
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

/** Read the "Shared with me" list from the cache the sidebar's `list()` fills. */
export function useSharedConversations():
    | Page<ISharedConversationListItem>
    | undefined {
    const { data } = useQuery(sharedConversationsQueryOptions);
    return data;
}

export function useSharedConversation(remoteId: string | undefined) {
    const { data: first } = useQuery(sharedConversationsQueryOptions);
    const fromFirst = first?.items.find(
        (item) => item.conversation_id.toString() === remoteId
    );
    const { data } = useQuery({
        ...sharedConversationQueryOptions(remoteId ?? ""),
        enabled: Boolean(remoteId && first && !fromFirst),
    });
    return fromFirst ?? data;
}
